import { Icon } from '../../atoms/Icon';
import { Typography } from '../Typography';

type DifficultyLevel = 'alto' | 'medio' | 'baixo';

interface AccuracyDifficultyProps {
  /** Taxa de acerto entre 0 e 1, como vem da API. Sem valor = sem respostas. */
  correctPercentage?: number | null;
  className?: string;
}

// Classes fixas por nível para o Tailwind conseguir gerá-las.
const levelStyles: Record<
  DifficultyLevel,
  {
    label: string;
    card: string;
    text: string;
    badge: string;
    dot: string;
    bar: string;
  }
> = {
  alto: {
    label: 'Alto',
    card: 'border-feedback-color-error-lighter bg-feedback-color-error-lightest/40',
    text: 'text-feedback-color-error-pure',
    badge: 'border-feedback-color-error-lighter text-feedback-color-error-pure',
    dot: 'bg-feedback-color-error-pure',
    bar: 'bg-feedback-color-error-pure',
  },
  medio: {
    label: 'Médio',
    card: 'border-feedback-color-warning-darkest bg-feedback-color-warning-lightest/40',
    text: 'text-feedback-color-warning-darkest',
    badge:
      'border-feedback-color-warning-lighter text-feedback-color-warning-darkest',
    dot: 'bg-feedback-color-warning-darkest',
    bar: 'bg-feedback-color-warning-darkest',
  },
  baixo: {
    label: 'Baixo',
    card: 'border-feedback-color-success-lighter bg-feedback-color-success-lightest/40',
    text: 'text-feedback-color-success-pure',
    badge:
      'border-feedback-color-success-lighter text-feedback-color-success-pure',
    dot: 'bg-feedback-color-success-pure',
    bar: 'bg-feedback-color-success-pure',
  },
};

// Alto: 0 a 30% · Médio: 31 a 70% · Baixo: 71 a 100%
const getDifficultyLevel = (percentage: number): DifficultyLevel => {
  if (percentage <= 30) return 'alto';
  if (percentage <= 70) return 'medio';
  return 'baixo';
};

// Questão que ninguém respondeu ainda: convida o aluno a ser o primeiro.
const NoAnswersCard = ({ className = '' }: { className?: string }) => (
  <div
    className={`flex w-full max-w-[450px] flex-col gap-xs rounded-lg border-[1px] border-brand-color-primary-lighter bg-brand-color-primary-lighter/20 px-4 py-3 ${className}`}
  >
    <div className='flex items-center justify-between gap-sm'>
      <div className='flex items-center gap-xs'>
        <Icon
          type='material'
          Icon='emoji_events'
          className='text-lg text-brand-color-primary-pure dark:text-card-color-default'
        />
        <Typography type='title' size='h4'>
          Desafio lançado!
        </Typography>
      </div>
      <span className='rounded-pill flex dark:bg-card-color-default items-center gap-xxs border-hairline border-brand-color-primary-lighter bg-mono-color-white px-2 py-0.5 text-sm text-brand-color-primary-pure'>
        <span className='rounded-pill h-2 w-2 bg-brand-color-primary-pure' />
        Sem respostas
      </span>
    </div>
    <Typography size='small' className='text-mono-color-darkest'>
      Seja o primeiro aluno a responder essa questão.
    </Typography>
    <div className='rounded-pill h-[3px] w-full bg-brand-color-primary-lighter' />
  </div>
);

export const AccuracyDifficulty = ({
  correctPercentage,
  className = '',
}: AccuracyDifficultyProps) => {
  if (!correctPercentage) return <NoAnswersCard className={className} />;

  const percentage = Math.min(Math.max(correctPercentage * 100, 0), 100);
  const level = getDifficultyLevel(percentage);
  const styles = levelStyles[level];

  const formattedPercentage = `${percentage.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

  return (
    <div
      className={`flex w-full max-w-[450px] flex-col gap-xs rounded-lg border-[1px] px-4 py-3 ${styles.card} ${className}`}
    >
      <div className='flex items-center justify-between gap-sm'>
        <Typography type='title' size='h4'>
          Nível do desafio
        </Typography>
        <span
          className={`rounded-pill flex items-center gap-xxs border-hairline bg-mono-color-white px-2 py-0.5 text-sm ${styles.badge}`}
        >
          <span className={`rounded-pill h-2 w-2 ${styles.dot}`} />
          {styles.label}
        </span>
      </div>
      <Typography size='small' className='text-mono-color-darkest'>
        <strong className={`text-md ${styles.text}`}>
          {formattedPercentage}
        </strong>{' '}
        de quem já tentou <strong>acertou</strong> essa questão.{' '}
        <strong>Bora?</strong>
      </Typography>
      <div className='rounded-pill h-[3px] w-full overflow-hidden bg-mono-color-lighter'>
        <div
          className={`rounded-pill h-full ${styles.bar}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};
